import * as THREE from "three";
import Engine from './Engine';
import MaterialAsset from './MaterialAsset';
import AudioSource from './AudioSource';
import ScopeCamera from './Camera';
import DefaultSceneManager from './DefaultSceneManager';
import {GroupsManager} from './GroupsManager';
import Utils from './Utilities';
import Scenario from './scenarioElements/Scenario';
import Text3D from './Text3D';
import Callout from './Callout';
import ARTracker from './ARTracker';
import ProjectSaver from './ProjectSaver';
import { assert } from './utilities/ErrorHandling';
import MathUtils from './utilities/MathUtils';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import TransformUtils from './utilities/TransformUtils';
import ProjectLoader from './ProjectLoader';
import ScenarioHealthUtils from './utilities/ScenarioHealthUtils';
import StandbyOverlay from "../components/layout/StandbyOverlay";
import { LightTypes, MaterialTypes, defaultTriggerObject, SaveModificationTypes, WorldUnitScales } from '../globals';
import Store, { StoreActions } from '../state/store';
import AssetUtility from '../classes/AssetUtility';
import ScopeLight from '../classes/Light';
import {Timeline} from '../classes/timeline/Timeline';

const guaranteedSaveRoots = [];
const referencedObjects = [];
const defaultSelectionRootToSequence = new Map();
const extrudedTextObjects = [];

export default class LegacyProjectLoader
{
	loadProjectData(projectData, projectId)
	{
		return new Promise((resolve, reject) =>
		{
			try
			{
				const uniqueTextureIds = [];
				const textureLoadPromises = [];
				const uniqueVideoIds = [];

				for (const texture of projectData.textures)
				{
					if (!uniqueTextureIds.includes(texture.assetId))
					{
						uniqueTextureIds.push(texture.assetId);
						textureLoadPromises.push(MaterialAsset.loadTexture(texture.assetId));
					}
				}

				if (projectData.materials)
				{
					for (const material of projectData.materials)
					{
						if (material.extras.type === MaterialTypes.Video && material.extras.asset)
						{
							if (!uniqueVideoIds.includes(material.extras.asset))
							{
								uniqueVideoIds.push(material.extras.asset);
								textureLoadPromises.push(MaterialAsset.loadVideoTexture(material.extras.asset));
							}
						}
					}
				}

				console.log("loading project textures...");
				Promise.all(textureLoadPromises).then(() =>
				{
					console.log("loading project materials...");
					if (projectData.materials)
					{
						for (const material of projectData.materials)
						{
							if (material.extras.guid !== MaterialAsset.defaultMaterialGuid) new MaterialAsset(material);
						}
					}

					console.log("loading project models...");
					const modelLoadPromises = [];
					if (projectData.extras)
					{
						if (projectData.extras.modelAssetIds)
						{
							for (const assetId of projectData.extras.modelAssetIds)
							{
								const url = AssetUtility.getProcessedAssetURL(assetId);
								const assetName = AssetUtility.getAssetName(assetId);
								modelLoadPromises.push(Engine.getInstance().prepareModel(assetId, url, assetName, null, false, false));
							}
						}
					}
					Promise.all(modelLoadPromises).then(() =>
					{
						StandbyOverlay.updateStandbyModeMessage("Loading Project", true);
						setTimeout(() => 
						{
							this.onAssetsLoaded(projectData).then(() =>
							{
								//Perform health check
								ScenarioHealthUtils.FullHealthCheck();
								resolve();
							}, 
							(error) =>
							{
								reject(error);
							});
						}, 0);

					},
					function(error)
					{
						console.error("Could not load all models:", error);
						ProjectLoader.projectLoadFailed(projectId, error);
						reject(error);
					});
				},
				function(error)
				{
					console.error("Could not load all textures:", error);
					ProjectLoader.projectLoadFailed(projectId, error);
					reject(error);
				});
			}
			catch (error)
			{
				console.error('project loading failed', error);
				ProjectLoader.projectLoadFailed(projectId, error);
				reject(error);
			}
		});
	}

	onAssetsLoaded(projectData)
	{
		return new Promise((resolve, reject) =>
		{
			try
			{
				const worldUnits = Store.getState().preferencesModule.editorPreferences.unitDisplay;
				Engine.getInstance().setDisplayUnitScale(worldUnits ?? WorldUnitScales.Meters);

				if (projectData.scenes)
				{
					for (let i = 0; i < projectData.scenes.length; i++)
					{
						if (projectData.scenes[i].extras.isDefaultScene) this.loadScene(projectData, i, true);
					}
					for (let i = 0; i < projectData.scenes.length; i++)
					{
						if (!projectData.scenes[i].extras.isDefaultScene) this.loadScene(projectData, i, false);
					}
				}
	
				defaultSelectionRootToSequence.clear();
	
				//fix corrupted UI data in old projects
				const uiStepIds = [...Object.keys(projectData.extras.uiData)];
				for (const uiStepId of uiStepIds)
				{
				//step doesn't exist, delete the ui data
					if (!projectData.extras.sequenceData.steps[uiStepId])
					{
						delete projectData.extras.uiData[uiStepId];
						continue;
					}
	
					//step exists and ui data has no actual data - filling the blanks hardcoded
					if (projectData.extras.uiData[uiStepId].active && projectData.extras.uiData[uiStepId].active === 'Blank' && !projectData.extras.uiData[uiStepId][projectData.extras.uiData[uiStepId].active])
					{
						projectData.extras.uiData[uiStepId][projectData.extras.uiData[uiStepId].active] = {
							NextStep: { visible: { name: 'visible', type: 'toggle', value: 'true', default: 'true' } },
							PreviousStep: { visible: { name: 'visible', type: 'toggle', value: 'true', default: 'true' } },
							Voice: { visible: { name: 'Voice', type: 'text', value: '', default: null } }
						};
					}
	
					//old project with content ui, need to check if the headers have individual colors and backgrounds or is it the old shared value
					if (projectData.extras.uiData[uiStepId].active && projectData.extras.uiData[uiStepId].active === 'Content')
					{
						const uiHeaders = projectData.extras.uiData[uiStepId][projectData.extras.uiData[uiStepId].active].Headers;
						let headerCounter = 1;
						while (uiHeaders[`header ${headerCounter.toString()} text`])
						{
							if (uiHeaders['text color'] && !uiHeaders[`header ${headerCounter.toString()} text color`])
							{
								uiHeaders[`header ${headerCounter.toString()} text color`] = { ...uiHeaders['text color'] };
							}
							if (uiHeaders['background color'] && !uiHeaders[`header ${headerCounter.toString()} background color`])
							{
								uiHeaders[`header ${headerCounter.toString()} background color`] = { ...uiHeaders['background color'] };
							}
							if (uiHeaders['shadow color'] && !uiHeaders[`header ${headerCounter.toString()} shadow color`])
							{
								uiHeaders[`header ${headerCounter.toString()} shadow color`] = { ...uiHeaders['shadow color'] };
							}
							if (uiHeaders['text alpha'] && !uiHeaders[`header ${headerCounter.toString()} text alpha`])
							{
								uiHeaders[`header ${headerCounter.toString()} text alpha`] = { ...uiHeaders['text alpha'] };
							}
							if (uiHeaders['background alpha'] && !uiHeaders[`header ${headerCounter.toString()} background alpha`])
							{
								uiHeaders[`header ${headerCounter.toString()} background alpha`] = { ...uiHeaders['background alpha'] };
							}
							if (uiHeaders['shadow alpha'] && !uiHeaders[`header ${headerCounter.toString()} shadow alpha`])
							{
								uiHeaders[`header ${headerCounter.toString()} shadow alpha`] = { ...uiHeaders['shadow alpha'] };
							}
							headerCounter++;
						}
					}
				}
	
				GroupsManager.load_old(projectData.extras.groups);
				Timeline.loadTimelines(projectData.extras.timelineData);
				if (projectData.extras.slideMaster)
				{
					const uiSlideIds = [...Object.keys(projectData.extras.slideMaster)];
					for (const uiSlideId of uiSlideIds)
					{
					//corrupted slides of corrupted old projects headers need to be repaired aswell
						if (projectData.extras.slideMaster[uiSlideId].active && projectData.extras.slideMaster[uiSlideId].active === 'Content')
						{
							const uiHeaders = projectData.extras.slideMaster[uiSlideId][projectData.extras.slideMaster[uiSlideId].active].Headers;
							let headerCounter = 1;
							while (uiHeaders[`header ${headerCounter.toString()} text`])
							{
								if (uiHeaders['text color'] && !uiHeaders[`header ${headerCounter.toString()} text color`])
								{
									uiHeaders[`header ${headerCounter.toString()} text color`] = { ...uiHeaders['text color'] };
								}
								if (uiHeaders['background color'] && !uiHeaders[`header ${headerCounter.toString()} background color`])
								{
									uiHeaders[`header ${headerCounter.toString()} background color`] = { ...uiHeaders['background color'] };
								}
								if (uiHeaders['shadow color'] && !uiHeaders[`header ${headerCounter.toString()} shadow color`])
								{
									uiHeaders[`header ${headerCounter.toString()} shadow color`] = { ...uiHeaders['shadow color'] };
								}
								if (uiHeaders['text alpha'] && !uiHeaders[`header ${headerCounter.toString()} text alpha`])
								{
									uiHeaders[`header ${headerCounter.toString()} text alpha`] = { ...uiHeaders['text alpha'] };
								}
								if (uiHeaders['background alpha'] && !uiHeaders[`header ${headerCounter.toString()} background alpha`])
								{
									uiHeaders[`header ${headerCounter.toString()} background alpha`] = { ...uiHeaders['background alpha'] };
								}
								if (uiHeaders['shadow alpha'] && !uiHeaders[`header ${headerCounter.toString()} shadow alpha`])
								{
									uiHeaders[`header ${headerCounter.toString()} shadow alpha`] = { ...uiHeaders['shadow alpha'] };
								}
								headerCounter++;
							}
						}
					}
					Store.dispatch(StoreActions.ui.loadUIData(projectData.extras.uiData));
					Store.dispatch(StoreActions.ui.loadSlideMasterData({ ui: projectData.extras.slideMaster }));
				}
				else
				{
					Store.dispatch(StoreActions.ui.loadSlideMasterData({ shouldConvert: projectData.extras.sequenceData, ui: projectData.extras.uiData }));
				}
	
				this.convertOldProject();
	
				Store.dispatch(StoreActions.checklist.loadChecklist(projectData.extras.checklistData));
				Store.dispatch(StoreActions.checklist.setQuizSettings(projectData.quizSettings));
				Store.dispatch(StoreActions.scenario.loadStepConditions(projectData.stepConditions));
				Scenario.current().load(projectData.extras.sequenceData, projectData.extras.trackersData);
				ARTracker.load(projectData.extras.trackersData);
				resolve();
			}
			catch (error)
			{
				reject(error);
			}
		});
	}

	convertOldProject()
	{
		function analyzeAssetHierarchy(object, saveRoot = object, srcObject = undefined)
		{
			if (!srcObject)
			{
				const assetRoot = Engine.getInstance().getModelAsset(object.userData.assetId);
				const srcNodeIndex = object.userData.modelData.nodeIndex;
				const srcPrimitiveIndex = object.userData.modelData.primitiveIndex;
				srcObject = SceneObjectUtils.getSubNode(assetRoot, srcNodeIndex, srcPrimitiveIndex, object.userData.assetId);
			}

			const childData = [];
			let noneDefaultCounter = 0;
			for (let i = 0; i < object.children.length; i++)
			{
				const child = object.children[i];
				if (child.userData.modelData && !child.userData.hideInHierarchy && !guaranteedSaveRoots.includes(child))
				{
					const data = {
						nodeIndex: child.userData.modelData.nodeIndex,
						primitiveIndex: child.userData.modelData.primitiveIndex,
						childIndex: i,
						name: child.name,
					};

					if (child.userData.defaultObjectGuid)
					{
						childData.splice(noneDefaultCounter, 0, data);
					}
					else
					{
						childData.unshift(data);
						noneDefaultCounter++;
					}
				}
			}
			//first go over all objects with defaultObjectGuid, in the order in which they appear in the child array
			//then over objects without default

			const srcChildData = [];
			for (let i = 0; i < srcObject.children.length; i++)
			{
				const child = srcObject.children[i];
				if (child.userData.modelData && !child.userData.hideInHierarchy)
				{
					const data = {
						nodeIndex: child.userData.modelData.nodeIndex,
						primitiveIndex: child.userData.modelData.primitiveIndex,
						childIndex: i,
					};
					srcChildData.push(data);
				}
			}

			for (let i = childData.length-1; i >= 0; i--)
			{
				for (let j = srcChildData.length-1; j >= 0; j--)
				{
					if (childData[i].nodeIndex === srcChildData[j].nodeIndex && childData[i].primitiveIndex === srcChildData[j].primitiveIndex)
					{
						const matchedObject = object.children[childData[i].childIndex];
						const matchingSrc = srcObject.children[srcChildData[j].childIndex];

						if (matchedObject.userData.unlinkedDefaultObjectGuid) delete matchedObject.userData.unlinkedDefaultObjectGuid;

						childData.splice(i, 1);
						srcChildData.splice(j, 1);
						analyzeAssetHierarchy(matchedObject, saveRoot, matchingSrc);
						break;
					}
				}
			}

			for (const unMatchedChildData of childData)
			{
				guaranteedSaveRoots.push(object.children[unMatchedChildData.childIndex]);
			}

			for (const unMatchedSrcData of srcChildData)
			{
				if (!saveRoot.userData.removedSubNodes) saveRoot.userData.removedSubNodes = [];

				saveRoot.userData.removedSubNodes.push({ nodeIndex: unMatchedSrcData.nodeIndex, primitiveIndex: unMatchedSrcData.primitiveIndex });
			}
		}

		for (const saveRoot of guaranteedSaveRoots)
		{
			ProjectSaver.getInstance().markAsSaveRoot(saveRoot);
			if (saveRoot.parent.parent.type !== 'Scene')
			{
				ProjectSaver.getInstance().markModifiedObject(saveRoot.parent, SaveModificationTypes.Reference);
			}

			if (saveRoot.userData.unlinkedDefaultObjectGuid)
			{
				const defaultObject = DefaultSceneManager.getInstance().findDefaultObject(saveRoot.userData.unlinkedDefaultObjectGuid);
				if (defaultObject) ProjectSaver.getInstance().markModifiedObject(defaultObject, SaveModificationTypes.Reference);
			}

			if (saveRoot.userData.assetId)
			{
				analyzeAssetHierarchy(saveRoot);
			}
		}

		for (const refObject of referencedObjects)
		{
			const object = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(refObject.sceneGuid), refObject.guid);
			if (object) ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Reference);
		}

		for (const extrudedTextObject of extrudedTextObjects)
		{
			extrudedTextObject.offsetExtrudedText();
		}

		guaranteedSaveRoots.length = 0;
		referencedObjects.length = 0;
		extrudedTextObjects.length = 0;
		defaultSelectionRootToSequence.clear();
	}

	loadScene(projectData, sceneIndex, isDefaultScene)
	{
		const sceneGuid = projectData.scenes[sceneIndex].extras.guid;
		const sceneParams = { existingGuid: sceneGuid };

		Engine.getInstance().createScene(sceneParams);
		const scene = Engine.getInstance().getScene(sceneGuid);

		assert(projectData.scenes[sceneIndex].nodes.length === 1);//our scenes always contain a single node which corresponds to the selectionRoot

		const selectionRootNodeIndex = projectData.scenes[sceneIndex].nodes[0];
		const selectionRootNode = projectData.nodes[selectionRootNodeIndex];
		const selectionRoot = Engine.getInstance().getSelectionRoot(scene.userData.guid);
		selectionRoot.userData.guid = selectionRootNode.extras.guid;
		selectionRoot.userData.visibility = selectionRootNode.extras.visibility;

		const sequenceId = this.getSequenceId(projectData, sceneGuid);
		if (isDefaultScene) defaultSelectionRootToSequence.set(sequenceId, selectionRoot.userData.guid);

		if (selectionRootNode.extras.defaultObjectGuid && !isDefaultScene)
		{
			DefaultSceneManager.getInstance().onLinkedObjectLoaded_old(selectionRoot, sceneGuid, defaultSelectionRootToSequence.get(sequenceId));
		}

		const animationModelRoots = [];
		const skinnedMeshes = [];

		if (selectionRootNode.children)
		{
			for (const childNodeIndex of selectionRootNode.children)
			{
				this.loadNode(projectData, childNodeIndex, selectionRoot, animationModelRoots, skinnedMeshes, sceneGuid, isDefaultScene);
			}
		}

		for (const animatedObject of animationModelRoots)
		{
			SceneObjectUtils.correctObjectAnimations(animatedObject);

			if (animatedObject.userData.animations) //Utils.correctObjectAnimations may have rejceted the animations due to containing null data
			{
				for (const anim of animatedObject.userData.animations)
				{
					anim.sceneGuid = sceneGuid;
				}
			}

			Engine.getInstance().registerObjectAnimationsForExport(animatedObject);
		}

		for (const skinnedMesh of skinnedMeshes)
		{
			SceneObjectUtils.reconstructSkeleton(skinnedMesh);
		}

		if (isDefaultScene)
		{
			DefaultSceneManager.getInstance().onDefaultSceneLoaded_old(scene);
		}
		else if (projectData.scenes[sceneIndex].extras.defaultSceneGuid)
		{
			DefaultSceneManager.getInstance().onLinkedSceneLoaded(scene, projectData.scenes[sceneIndex].extras.defaultSceneGuid);
		}
	}

	loadNode(projectData, nodeIndex, parent, animationModelRoots, skinnedMeshes, sceneGuid, isDefaultScene)
	{
		const node = projectData.nodes[nodeIndex];
		if (node.extras.hideInHierarchy) return;

		let isLinkedObject = (node.extras.defaultObjectGuid !== undefined && !isDefaultScene);
		let originatingObject = isLinkedObject ? DefaultSceneManager.getInstance().findDefaultObject(node.extras.defaultObjectGuid) : null;
		if (isLinkedObject)
		{
			if (!originatingObject || !DefaultSceneManager.getInstance().isValidLinkedObject_old(parent, sceneGuid, originatingObject))
			{
				isLinkedObject = false;
				originatingObject = null;
			}
		}

		const isSaveRoot = (!node.extras.assetId ||
			node.extras.assetId !== parent.userData.assetId ||
			(parent.userData.defaultObjectGuid && !isLinkedObject) ||
			(node.extras.modelData.nodeIndex === undefined && node.extras.modelData.primitiveIndex === undefined));
		const trackModifications = (isLinkedObject || !isSaveRoot);


		let destObject = null;
		const assetId = node.extras.assetId;
		if (assetId)
		{
			const assetRoot = Engine.getInstance().getModelAsset(assetId);
			const srcNodeIndex = node.extras.modelData.nodeIndex;
			const srcPrimitiveIndex = node.extras.modelData.primitiveIndex;
			const srcObject = SceneObjectUtils.getSubNode(assetRoot, srcNodeIndex, srcPrimitiveIndex, assetId);
			destObject = SceneObjectUtils.cloneNoneRecursive(srcObject);
			destObject.position.set(0, 0, 0);
			destObject.quaternion.set(0, 0, 0, 1);
			destObject.scale.set(1, 1, 1);
			if (destObject.userData.camera)
			{
				destObject.onCameraAddedToScene(sceneGuid);
				ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Reference);
			}
			if (srcObject.isSkinnedMesh)
			{
				skinnedMeshes.push(destObject);
			}

			if (!originatingObject) originatingObject = srcObject;
		}
		else
		{
			if (node.extras.primitiveType)
			{
				const geometry = Utils.getPrimitiveGeometry(node.extras.primitiveType);
				destObject = new THREE.Mesh(geometry);
				destObject.userData.primitiveType = node.extras.primitiveType;
			}
			else if (node.extras.lightType)
			{
				if (node.extras.lightType === LightTypes.Directional)
				{
					destObject = new ScopeLight(node.extras.lightType);
					destObject.setLightAngle(node.extras.light.object.angle);
					destObject.setLightRange(node.extras.light.object.distance);
					destObject.setLightColor(node.extras.lightColor);
					destObject.setLightIntensity(node.extras.lightIntensity);

					if (trackModifications && (destObject.userData.lightColor !== originatingObject.userData.lightColor ||
												destObject.userData.lightIntensity !== originatingObject.userData.lightIntensity))
					{
						ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Light);
					}
				}
				else //disabled spot/point lights - doing this for backwards comptability
				{
					destObject = new THREE.Object3D();
				}
			}
			else if (node.extras.audio)
			{
				destObject = new AudioSource(Engine.getInstance().getAudioListener());
				destObject.setAudio3D(node.extras.audio.is3D);
				destObject.setAudioVolume(node.extras.audio.volume);
				destObject.setAudioClip(node.extras.audio.clip);
				destObject.setAudioRange(node.extras.audio.range);
				if (trackModifications && JSON.stringify(originatingObject.userData.audio) !== JSON.stringify(destObject.userData.audio))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Audio);
				}

			}
			else if (node.extras.camera)
			{
				destObject = new ScopeCamera();
				destObject.userData.camera.nearplane = node.extras.camera.nearplane;
				destObject.userData.camera.farPlane = node.extras.camera.farPlane;
				destObject.userData.camera.fov = node.extras.camera.fov;
				destObject.userData.camera.orthographicSize = node.extras.camera.orthographicSize;
				if (node.extras.isInteractiveModeCamera) destObject.userData.isInteractiveModeCamera = node.extras.isInteractiveModeCamera;
				if (node.extras.preInteractiveBackupValues) destObject.userData.preInteractiveBackupValues = node.extras.preInteractiveBackupValues;
				if (node.extras.camera.aspect) destObject.setCameraAspect(node.extras.camera.aspect);

				destObject.setCameraProjectionMode(node.extras.camera.projection);
				destObject.onCameraAddedToScene(sceneGuid);

				if (trackModifications && JSON.stringify(originatingObject.userData.camera) !== JSON.stringify(destObject.userData.camera))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Camera);
				}
			}
			else if (node.extras.text3d)
			{
				destObject = new Text3D();
				destObject.userData.text3d = node.extras.text3d;
				destObject.setTextColor(destObject.userData.text3d.color);
				destObject.setTextAnchor(destObject.userData.text3d.anchor);
				destObject.generateTextGeometry();
				Text3D.addToText3dBank(node.guid, destObject);
				extrudedTextObjects.push(destObject);

				if (trackModifications && JSON.stringify(originatingObject.userData.text3d) !== JSON.stringify(destObject.userData.text3d))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Text3D);
				}
			}
			else
			{
				destObject = new THREE.Object3D();
			}
		}

		assert(originatingObject || !trackModifications);

		if (isSaveRoot) guaranteedSaveRoots.push(destObject);

		destObject.userData.visibility = node.extras.visibility;
		destObject.userData.guid = node.extras.guid;
		destObject.name = node.name;
		parent.add(destObject);

		if (node.translation) destObject.position.fromArray(node.translation);
		if (node.rotation) destObject.quaternion.fromArray(node.rotation);
		if (node.scale) destObject.scale.fromArray(node.scale);

		if (trackModifications)
		{
			if (!MathUtils.areVectorsEqual(originatingObject.position, destObject.position)) ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Position);
			if (!MathUtils.areVectorsEqual(originatingObject.rotation, destObject.rotation)) ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Rotation);
			if (!MathUtils.areVectorsEqual(originatingObject.scale, destObject.scale)) ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Scale);
			if (JSON.stringify(originatingObject.userData.visibility) !== JSON.stringify(destObject.userData.visibility)) ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Visibility);
			if (originatingObject.name !== destObject.name) ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Name);
		}

		if (node.mesh !== undefined)
		{
			const meshIndex = node.mesh;
			const mesh = projectData.meshes[meshIndex];
			if (mesh.primitives[0].material !== undefined)
			{
				const materialIndex = mesh.primitives[0].material;
				const materialAssetGuid = projectData.materials[materialIndex].extras.guid;
				const materialAsset = MaterialAsset.getMaterialAssetByGuid(materialAssetGuid);
				MaterialAsset.assignMaterialAssetToObject(materialAsset, destObject);

				if (trackModifications)
				{
					if (materialAsset !== MaterialAsset.getMaterialAssetByObjectGuid(originatingObject.userData.guid))
					{
						ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Material);
					}
				}
			}
			else if (node.extras.primitiveType)
			{
				MaterialAsset.assignMaterialAssetToObject(MaterialAsset.defaultMaterial, destObject);
			}
		}

		if (isLinkedObject && !isDefaultScene)
		{
			DefaultSceneManager.getInstance().onLinkedObjectLoaded_old(destObject, sceneGuid, originatingObject);
		}
		else if (node.extras.unlinkedDefaultObjectGuid)
		{
			destObject.userData.unlinkedDefaultObjectGuid = node.extras.unlinkedDefaultObjectGuid;
		}

		if (node.extras.annotation)
		{
			Callout.loadCallout(destObject, node.extras.annotation);
			if (trackModifications)
			{
				if (!originatingObject.userData.annotation || JSON.stringify(originatingObject.userData.annotation) !== JSON.stringify(destObject.userData.annotation))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Callout);
				}
			}
		}

		if (node.extras.triggers)
		{
			destObject.userData.triggers = [];
			for (const trigger of node.extras.triggers)
			{
				destObject.userData.triggers.push({ ...JSON.parse(JSON.stringify(defaultTriggerObject)), ...trigger });
				if (trigger.targetObject) referencedObjects.push({ guid: trigger.targetObject, sceneGuid: sceneGuid });
			}

			if (trackModifications)
			{
				if (!originatingObject.userData.triggers || JSON.stringify(originatingObject.userData.triggers) !== JSON.stringify(destObject.userData.triggers))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Trigger);
				}
			}
		}

		if (node.extras.pivot)
		{
			TransformUtils.loadPivot(destObject, node.extras.pivot.relativeOffset, node.extras.pivot.geometryOffset, node.extras.pivot.space);
			if (trackModifications)
			{
				if (!originatingObject.userData.pivot || JSON.stringify(originatingObject.userData.pivot) !== JSON.stringify(destObject.userData.pivot))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Pivot);
				}
			}
		}

		if (node.extras.billboard)
		{
			destObject.userData.billboard = node.extras.billboard;
			if (destObject.userData.billboard.enabled)
			{
				Engine.getInstance().getBillboardManager().enableBillboard(destObject, sceneGuid);
			}
			if (destObject.userData.billboard.targetGuid) referencedObjects.push({ guid: destObject.userData.billboard.targetGuid, sceneGuid: sceneGuid });
			if (trackModifications)
			{
				if (!originatingObject.userData.billboard || JSON.stringify(originatingObject.userData.billboard) !== JSON.stringify(destObject.userData.billboard))
				{
					ProjectSaver.getInstance().markModifiedObject(destObject, SaveModificationTypes.Billboard);
				}
			}
		}

		if (destObject.userData.animations)
		{
			animationModelRoots.push(destObject);
		}

		SceneObjectUtils.initialiseObjectProperties(destObject);
		
		if (node.children)
		{
			for (const childNodeIndex of node.children)
			{
				this.loadNode(projectData, childNodeIndex, destObject, animationModelRoots, skinnedMeshes, sceneGuid, isDefaultScene);
			}
		}
	}

	getSequenceId(projectData, sceneGuid)
	{
		for (const step of Object.values(projectData.extras.sequenceData.steps))
		{
			if (step.sceneId === sceneGuid)
			{
				return step.parent;
			}
		}
		return null;
	}
}

